// Поля вводу: Input, Textarea, Select, DateInput, TimeInput, SearchInput — спільний клас .field (index.css).
// Працюють і самостійно, і всередині FormField (form.tsx) — id/aria-invalid передаються пропсами.
import type { LucideIcon } from 'lucide-react';
import { Search, X } from 'lucide-react';
import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from '@/lib/cn';

type FieldSize = 'sm' | 'md';

export interface InputProps extends Omit<ComponentPropsWithRef<'input'>, 'size'> {
  size?: FieldSize;
  invalid?: boolean;
  /** Іконка зліва всередині поля. */
  icon?: LucideIcon;
  /** Елемент справа всередині поля (одиниця виміру, кнопка). */
  suffix?: ReactNode;
}

export function Input({ size = 'md', invalid, icon: Icon, suffix, className, ...rest }: InputProps) {
  const input = (
    <input
      aria-invalid={invalid || undefined}
      className={cn('field', size === 'sm' && 'field-sm', Icon && (size === 'sm' ? 'pl-8' : 'pl-9'), suffix ? 'pr-10' : null, !Icon && !suffix && className)}
      {...rest}
    />
  );
  if (!Icon && !suffix) return input;
  return (
    <div className={cn('relative', className)}>
      {Icon ? (
        <Icon
          className={cn('pointer-events-none absolute top-1/2 -translate-y-1/2 text-faint', size === 'sm' ? 'left-2.5 size-4' : 'left-3 size-4')}
          aria-hidden
        />
      ) : null}
      {input}
      {suffix ? <div className="absolute inset-y-0 right-1 flex items-center">{suffix}</div> : null}
    </div>
  );
}

export interface TextareaProps extends ComponentPropsWithRef<'textarea'> {
  invalid?: boolean;
}

export function Textarea({ invalid, className, rows = 3, ...rest }: TextareaProps) {
  return <textarea rows={rows} aria-invalid={invalid || undefined} className={cn('field', className)} {...rest} />;
}

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<ComponentPropsWithRef<'select'>, 'size' | 'children'> {
  size?: FieldSize;
  invalid?: boolean;
  options?: readonly SelectOption[];
  /** Групи опцій (optgroup). */
  groups?: ReadonlyArray<{ label: string; options: readonly SelectOption[] }>;
  /** Порожній пункт на початку (напр. «Не вибрано» / «Усі»). */
  placeholder?: string;
  children?: ReactNode;
}

export function Select({ size = 'md', invalid, options, groups, placeholder, className, children, ...rest }: SelectProps) {
  return (
    <select aria-invalid={invalid || undefined} className={cn('field', size === 'sm' && 'field-sm', className)} {...rest}>
      {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
      {options?.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled}>
          {o.label}
        </option>
      ))}
      {groups?.map((g) => (
        <optgroup key={g.label} label={g.label}>
          {g.options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </optgroup>
      ))}
      {children}
    </select>
  );
}

export interface DateInputProps extends Omit<InputProps, 'type' | 'value' | 'onChange' | 'icon' | 'suffix'> {
  /** 'YYYY-MM-DD' або '' */
  value: string;
  onChange: (value: string) => void;
}

/** Дата: значення 'YYYY-MM-DD'; браузер показує ДД.ММ.РРРР за мовою документа (lang="uk"). */
export function DateInput({ value, onChange, className, ...rest }: DateInputProps) {
  return <Input type="date" value={value} onChange={(e) => onChange(e.target.value)} className={cn('num', className)} {...rest} />;
}

export interface TimeInputProps extends Omit<InputProps, 'type' | 'value' | 'onChange' | 'icon' | 'suffix'> {
  /** 'HH:mm' або '' */
  value: string;
  onChange: (value: string) => void;
}

/** Час ГГ:ХХ (24-годинний). */
export function TimeInput({ value, onChange, className, ...rest }: TimeInputProps) {
  return <Input type="time" step={60} value={value} onChange={(e) => onChange(e.target.value)} className={cn('num', className)} {...rest} />;
}

export interface SearchInputProps extends Omit<InputProps, 'type' | 'value' | 'onChange' | 'icon' | 'suffix'> {
  value: string;
  onChange: (value: string) => void;
}

/** Поле пошуку з кнопкою очищення. */
export function SearchInput({ value, onChange, placeholder = 'Пошук…', size = 'sm', ...rest }: SearchInputProps) {
  return (
    <Input
      type="search"
      size={size}
      icon={Search}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && value) {
          e.stopPropagation();
          onChange('');
        }
      }}
      suffix={
        value ? (
          <button
            type="button"
            onClick={() => onChange('')}
            className="flex size-6 items-center justify-center rounded-md text-faint hover:bg-surface-3 hover:text-fg"
            aria-label="Очистити пошук"
          >
            <X className="size-3.5" />
          </button>
        ) : undefined
      }
      {...rest}
    />
  );
}
